import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronLeft, ChevronRight, GripVertical, Play, Trash2 } from "lucide-react";

import { IconButton } from "@/components/kit";
import { fmtDur } from "@/lib/hooks";
import { cn } from "@/lib/utils";

function Tile({ m, i, count, onMove, onRemove }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: m.id });
  const video = m.kind === "video";
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={`media-item-${m.id}`}
      className={cn("relative overflow-hidden rounded-xl border border-border bg-card transition-shadow",
        isDragging && "z-10 scale-[1.03] border-primary shadow-2xl")}>
      <div className="relative aspect-square bg-secondary">
        {m.thumb_url ? <img src={m.thumb_url} alt={`${video ? "Video" : "Photo"} ${i + 1}`} loading="lazy" draggable={false}
          className="absolute inset-0 h-full w-full object-cover" data-testid={`media-thumb-${m.id}`} /> : null}
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/90" />
        <span className="absolute left-2 top-2 flex h-7 min-w-7 items-center justify-center rounded-full bg-background/80 px-2 text-xs font-semibold text-primary">{i + 1}</span>
        <button type="button" ref={setActivatorNodeRef} {...listeners} {...attributes} aria-label={`Drag to reorder item ${i + 1}`}
          className="focus-ring absolute right-1.5 top-1.5 flex h-11 w-11 cursor-grab touch-none items-center justify-center rounded-full bg-background/70 text-foreground backdrop-blur active:cursor-grabbing"
          data-testid={`media-drag-handle-${m.id}`}>
          <GripVertical className="h-5 w-5" aria-hidden />
        </button>
        <div className="absolute inset-x-3 bottom-2 flex items-center gap-1.5">
          {video ? <Play className="h-3.5 w-3.5 fill-current text-foreground" aria-hidden /> : null}
          <p className="truncate text-sm font-medium">{video ? "Video" : "Photo"} {i + 1}</p>
          <p className="ml-auto shrink-0 text-xs text-card-foreground">{video ? fmtDur(m.duration) : `${m.width ?? ""}×${m.height ?? ""}`}</p>
        </div>
      </div>
      <div className="flex items-center justify-between px-1 py-0.5">
        <IconButton icon={ChevronLeft} label="Move earlier" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`media-move-up-${m.id}`} />
        <IconButton icon={ChevronRight} label="Move later" disabled={i === count - 1} onClick={() => onMove(i, 1)} data-testid={`media-move-down-${m.id}`} />
        <IconButton icon={Trash2} label="Remove" tint="text-muted-foreground hover:text-destructive" onClick={() => onRemove(m.id)} data-testid={`media-remove-${m.id}`} />
      </div>
    </li>
  );
}

export function MediaGrid({ media, onReorder, onRemove }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = media.map((m) => m.id);
  const move = (i, d) => {
    const j = i + d;
    if (j >= 0 && j < ids.length) onReorder(arrayMove(ids, i, j));
  };
  const end = ({ active, over }) => {
    if (over && active.id !== over.id) onReorder(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)));
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={end}>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4" data-testid="media-grid">
          {media.map((m, i) => <Tile key={m.id} m={m} i={i} count={media.length} onMove={move} onRemove={onRemove} />)}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
